import type {
  MapMode,
  MapType,
  MarkdownMapEditRequest,
  MapTravelMode,
  MapUnits,
} from './markdown-map';

import {
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  Grid,
  HStack,
  Input,
  NativeSelect,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { CenteredDialogContent } from '#components/ui/centered-dialog-content';
import { toaster } from '#components/ui/toaster';
import { useAppSettings } from '#providers/app-settings-provider';

import {
  buildGoogleMapsEmbedUrl,
  createDefaultMarkdownMapEditValues,
  getMarkdownMapValidationMessage,
  markdownMapEditValuesToBlockData,
} from './markdown-map';

export interface MarkdownMapEditModalProps {
  request: MarkdownMapEditRequest | null;
  onDismiss: () => void;
}

const MODE_OPTIONS: Array<{ label: string; value: MapMode }> = [
  { label: 'Place', value: 'place' },
  { label: 'View', value: 'view' },
  { label: 'Directions', value: 'directions' },
  { label: 'Street View', value: 'streetview' },
  { label: 'Search', value: 'search' },
];
const MAP_TYPE_OPTIONS: Array<{ label: string; value: MapType }> = [
  { label: 'Roadmap', value: 'roadmap' },
  { label: 'Satellite', value: 'satellite' },
];
const TRAVEL_MODE_OPTIONS: Array<{ label: string; value: MapTravelMode }> = [
  { label: 'Driving', value: 'driving' },
  { label: 'Walking', value: 'walking' },
  { label: 'Bicycling', value: 'bicycling' },
  { label: 'Transit', value: 'transit' },
  { label: 'Flying', value: 'flying' },
];
const UNITS_OPTIONS: Array<{ label: string; value: MapUnits }> = [
  { label: 'Metric', value: 'metric' },
  { label: 'Imperial', value: 'imperial' },
];

type FormState = ReturnType<typeof createDefaultMarkdownMapEditValues>;

function LabeledField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Text fontSize="xs" fontWeight="medium" color="fg.muted">
        {label}
      </Text>
      {children}
    </label>
  );
}

export function MarkdownMapEditModal({
  request,
  onDismiss,
}: MarkdownMapEditModalProps) {
  const open = request != null;
  const { googleMapsEmbedApiKey } = useAppSettings();
  const primaryRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState>(
    createDefaultMarkdownMapEditValues,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!request) {
      setSaving(false);
      setForm(createDefaultMarkdownMapEditValues());
      return;
    }

    setForm(request.initialValues);
    queueMicrotask(() => primaryRef.current?.focus());
  }, [request]);

  const blockData = useMemo(
    () => markdownMapEditValuesToBlockData(form),
    [form],
  );
  const validationMessage = getMarkdownMapValidationMessage(blockData);
  const previewUrl = useMemo(
    () =>
      googleMapsEmbedApiKey
        ? buildGoogleMapsEmbedUrl(blockData, googleMapsEmbedApiKey)
        : null,
    [blockData, googleMapsEmbedApiKey],
  );

  const handleClose = () => {
    if (saving) return;

    onDismiss();
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSave = async () => {
    if (!request) return;

    if (validationMessage) {
      toaster.create({
        title: 'Incomplete map',
        description: validationMessage,
        type: 'warning',
      });
      return;
    }

    setSaving(true);
    try {
      await request.onCommit(form);
      onDismiss();
    } catch (error) {
      toaster.create({
        title: 'Could not save map',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      size="xl"
    >
      <CenteredDialogContent maxW="min(92vw, 52rem)">
        <DialogHeader>
          <DialogTitle>
            {request?.mode === 'insert' ? 'Insert map' : 'Edit map'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={4} align="stretch">
            <Text fontSize="sm" color="fg.muted">
              Configure a Google Maps Embed block. The stored markdown remains a
              single <code>{'<np-map>'}</code> custom element with JSON config.
            </Text>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
              <LabeledField label="Mode">
                <NativeSelect.Root disabled={saving}>
                  <NativeSelect.Field
                    value={form.mode}
                    onChange={(e) =>
                      setField('mode', e.target.value as MapMode)
                    }
                  >
                    {MODE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </LabeledField>
              <LabeledField label="Height">
                <Input
                  type="number"
                  min={200}
                  max={640}
                  value={form.height}
                  onChange={(e) => setField('height', e.target.value)}
                  disabled={saving}
                />
              </LabeledField>
              <LabeledField label="Zoom">
                <Input
                  type="number"
                  min={0}
                  max={21}
                  value={form.zoom}
                  onChange={(e) => setField('zoom', e.target.value)}
                  disabled={saving}
                  placeholder="Optional"
                />
              </LabeledField>
              <LabeledField label="Map type">
                <NativeSelect.Root disabled={saving}>
                  <NativeSelect.Field
                    value={form.mapType}
                    onChange={(e) =>
                      setField('mapType', e.target.value as MapType)
                    }
                  >
                    {MAP_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </LabeledField>
            </Grid>

            <LabeledField label="Language">
              <Input
                type="text"
                value={form.language}
                onChange={(e) => setField('language', e.target.value)}
                disabled={saving}
                placeholder="Optional, e.g. en"
              />
            </LabeledField>

            {(form.mode === 'place' || form.mode === 'search') && (
              <LabeledField
                label={form.mode === 'place' ? 'Place query' : 'Search query'}
              >
                <Input
                  ref={primaryRef}
                  type="text"
                  value={form.query}
                  onChange={(e) => setField('query', e.target.value)}
                  disabled={saving}
                  placeholder={
                    form.mode === 'place'
                      ? 'Eiffel Tower, Paris France'
                      : 'record stores in Seattle'
                  }
                />
              </LabeledField>
            )}

            {form.mode === 'view' && (
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
                <LabeledField label="Center latitude">
                  <Input
                    ref={primaryRef}
                    type="number"
                    value={form.centerLat}
                    onChange={(e) => setField('centerLat', e.target.value)}
                    disabled={saving}
                    placeholder="37.4218"
                  />
                </LabeledField>
                <LabeledField label="Center longitude">
                  <Input
                    type="number"
                    value={form.centerLng}
                    onChange={(e) => setField('centerLng', e.target.value)}
                    disabled={saving}
                    placeholder="-122.0840"
                  />
                </LabeledField>
              </Grid>
            )}

            {form.mode === 'directions' && (
              <VStack gap={3} align="stretch">
                <LabeledField label="Origin">
                  <Input
                    ref={primaryRef}
                    type="text"
                    value={form.origin}
                    onChange={(e) => setField('origin', e.target.value)}
                    disabled={saving}
                    placeholder="Oslo Norway"
                  />
                </LabeledField>
                <LabeledField label="Destination">
                  <Input
                    type="text"
                    value={form.destination}
                    onChange={(e) => setField('destination', e.target.value)}
                    disabled={saving}
                    placeholder="Telemark Norway"
                  />
                </LabeledField>
                <LabeledField label="Waypoints">
                  <Input
                    type="text"
                    value={form.waypoints}
                    onChange={(e) => setField('waypoints', e.target.value)}
                    disabled={saving}
                    placeholder={'Berlin, Germany\nParis, France'}
                  />
                </LabeledField>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
                  <LabeledField label="Travel mode">
                    <NativeSelect.Root disabled={saving}>
                      <NativeSelect.Field
                        value={form.travelMode}
                        onChange={(e) => setField('travelMode', e.target.value)}
                      >
                        {TRAVEL_MODE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </LabeledField>
                  <LabeledField label="Units">
                    <NativeSelect.Root disabled={saving}>
                      <NativeSelect.Field
                        value={form.units}
                        onChange={(e) => setField('units', e.target.value)}
                      >
                        {UNITS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </NativeSelect.Field>
                    </NativeSelect.Root>
                  </LabeledField>
                </Grid>
                <LabeledField label="Avoid">
                  <Input
                    type="text"
                    value={form.avoid}
                    onChange={(e) => setField('avoid', e.target.value)}
                    disabled={saving}
                    placeholder="tolls, highways"
                  />
                </LabeledField>
              </VStack>
            )}

            {form.mode === 'streetview' && (
              <VStack gap={3} align="stretch">
                <LabeledField label="Location">
                  <Input
                    ref={primaryRef}
                    type="text"
                    value={form.location}
                    onChange={(e) => setField('location', e.target.value)}
                    disabled={saving}
                    placeholder="46.414382,10.013988"
                  />
                </LabeledField>
                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
                  gap={3}
                >
                  <LabeledField label="Heading">
                    <Input
                      type="number"
                      value={form.heading}
                      onChange={(e) => setField('heading', e.target.value)}
                      disabled={saving}
                      placeholder="210"
                    />
                  </LabeledField>
                  <LabeledField label="Pitch">
                    <Input
                      type="number"
                      min={-90}
                      max={90}
                      value={form.pitch}
                      onChange={(e) => setField('pitch', e.target.value)}
                      disabled={saving}
                      placeholder="10"
                    />
                  </LabeledField>
                  <LabeledField label="Field of view">
                    <Input
                      type="number"
                      min={10}
                      max={100}
                      value={form.fov}
                      onChange={(e) => setField('fov', e.target.value)}
                      disabled={saving}
                      placeholder="35"
                    />
                  </LabeledField>
                </Grid>
              </VStack>
            )}

            {form.mode !== 'view' && form.mode !== 'streetview' ? (
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
                <LabeledField label="Optional center latitude">
                  <Input
                    type="number"
                    value={form.centerLat}
                    onChange={(e) => setField('centerLat', e.target.value)}
                    disabled={saving}
                    placeholder="37.4218"
                  />
                </LabeledField>
                <LabeledField label="Optional center longitude">
                  <Input
                    type="number"
                    value={form.centerLng}
                    onChange={(e) => setField('centerLng', e.target.value)}
                    disabled={saving}
                    placeholder="-122.0840"
                  />
                </LabeledField>
              </Grid>
            ) : null}

            <Box>
              <Text fontSize="xs" fontWeight="medium" color="fg.muted" mb={2}>
                Preview
              </Text>
              {previewUrl ? (
                <Box>
                  <iframe
                    src={previewUrl}
                    title="Map preview"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    style={{
                      width: '100%',
                      minHeight: `${blockData.height}px`,
                      border: 0,
                      borderRadius: '12px',
                    }}
                  />
                </Box>
              ) : (
                <Box
                  borderWidth="1px"
                  borderStyle="dashed"
                  borderRadius="md"
                  px={4}
                  py={5}
                  color="fg.muted"
                >
                  <Text fontSize="sm">
                    {googleMapsEmbedApiKey
                      ? (validationMessage ?? 'Map preview unavailable.')
                      : 'Google Maps key is not configured for this environment.'}
                  </Text>
                </Box>
              )}
            </Box>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} flexWrap="wrap">
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={handleClose} disabled={saving}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button colorPalette="blue" onClick={handleSave} disabled={saving}>
              Save map
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
